/**
 * Componente para gestión CRUD de prioridades
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 5.4
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { ErpContext } from '../../../context/ErpContext';
import { SoporteContext } from '../../../context/SoporteContext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ModalCrearPrioridad from '../modals/ModalCrearPrioridad';

const GestionPrioridadesComponent = () => {
  const { serverURL } = useContext(ErpContext);
  const { reloadPrioridades } = useContext(SoporteContext);
  
  const [prioridades, setPrioridades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [prioridadEditar, setPrioridadEditar] = useState(null);
  
  useEffect(() => {
    cargarPrioridades();
  }, []);
  
  const cargarPrioridades = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${serverURL}/soporteAPI/prioridades`, {
        withCredentials: true,
        params: { includeInactive: 'true' }
      });
      
      // El backend puede devolver { success: true, prioridades: [...] } o directamente el array
      const prioridadesData = response.data.prioridades || response.data || [];
      setPrioridades(prioridadesData);
      
      // Recargar prioridades en el contexto si existe la función
      if (reloadPrioridades) {
        reloadPrioridades();
      }
    } catch (err) {
      console.error('Error cargando prioridades:', err);
      setError(err.response?.data?.error || 'Error al cargar las prioridades');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCrear = () => {
    setPrioridadEditar(null);
    setModalOpen(true);
  };
  
  const handleEditar = (prioridad) => {
    setPrioridadEditar(prioridad);
    setModalOpen(true);
  };
  
  const handleEliminar = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta prioridad?')) return;
    
    try {
      await axios.delete(`${serverURL}/soporteAPI/prioridades/${id}`, {
        withCredentials: true
      });
      setSuccess('Prioridad eliminada correctamente');
      cargarPrioridades();
    } catch (err) {
      console.error('Error eliminando prioridad:', err);
      setError(err.response?.data?.error || 'Error al eliminar la prioridad');
    }
  };
  
  const handleModalClose = (reload) => {
    setModalOpen(false);
    setPrioridadEditar(null);
    if (reload) {
      cargarPrioridades();
      setSuccess('Prioridad guardada correctamente');
    }
  };
  
  const columns = [
    { 
      field: 'nombre', 
      headerName: 'Nombre', 
      flex: 1,
      minWidth: 150
    },
    {
      field: 'firstResponseHours',
      headerName: 'First Response (h)',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0} horas
        </Typography>
      )
    },
    {
      field: 'resolutionHours',
      headerName: 'Resolution (h)',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0} horas
        </Typography>
      )
    },
    {
      field: 'color',
      headerName: 'Color',
      width: 120,
      renderCell: (params) => {
        const color = params.value || '#757575';
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: color,
                border: '1px solid',
                borderColor: 'divider'
              }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {color}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'orden',
      headerName: 'Orden',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'activa',
      headerName: 'Activa',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Sí' : 'No'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton 
              size="small" 
              onClick={() => handleEditar(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => handleEliminar(params.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];
  
  if (loading && prioridades.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestión de Prioridades</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Actualizar">
            <IconButton onClick={cargarPrioridades} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCrear}
          >
            Nueva Prioridad
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={prioridades}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 }
            }
          }}
          disableRowSelectionOnClick
          loading={loading}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>
      
      <ModalCrearPrioridad
        open={modalOpen}
        onClose={handleModalClose}
        prioridad={prioridadEditar}
      />
    </Paper>
  );
};

export default GestionPrioridadesComponent;

